// Production origin; keep sitemap and browser metadata on the same domain.
export const siteUrl = 'https://slick-slicer.com';

export const pages: Record<string, { title: string; description: string; type: string }> = {
  '/download': {
    title: 'Download Slick Slicer — Resin 3D Printing Software',
    description: 'Find Slick Slicer downloads and build availability. Our resin slicer is in development, with a free demo planned. Follow progress and contact the team.',
    type: 'WebPage',
  },
  '/': {
    title: 'Slick Slicer — Fast Resin 3D Printing Software',
    description: 'Meet Slick Slicer, GPU-accelerated resin 3D printing software built with Rust. Watch demos and help shape a fast, lightweight slicer in development.',
    type: 'WebPage',
  },
  '/pricing': {
    title: 'Pricing & Plans for Resin 3D Printing — Slick Slicer',
    description: 'Explore planned Standard and Enterprise options for Slick Slicer, included features and the roadmap. In development; subscriptions are not available yet.',
    type: 'WebPage',
  },
  '/faq': {
    title: 'Resin Slicer FAQ: Features, Demo & Licensing — Slick Slicer',
    description: 'Find answers about Slick Slicer: GPU acceleration, Rust, planned demo access, licensing and upcoming features for resin 3D printing.',
    type: 'WebPage',
  },
  '/contact': {
    title: 'Contact the Slick Slicer Team — Support & Feedback',
    description: 'Contact the Slick Slicer team in Poland for questions, feedback and enterprise inquiries. Email support or join our resin 3D printing community on Discord.',
    type: 'ContactPage',
  },
};

export function getSeo(pathname: string) {
  const path = pathname.replace(/\/+$/, '') || '/';
  const page = Object.prototype.hasOwnProperty.call(pages, path) ? pages[path] : undefined;
  const title = page?.title || 'Page Not Found — Slick Slicer';
  const description = page?.description || 'This page could not be found. Explore Slick Slicer, pricing, frequently asked questions or contact our team.';
  const canonical = page ? `${siteUrl}${path}` : undefined;
  const meta = [
    { name: 'description', content: description },
    { name: 'robots', content: page ? 'index, follow, max-image-preview:large' : 'noindex, follow' },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: 'Slick Slicer' },
    { property: 'og:locale', content: 'en_US' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:image', content: `${siteUrl}/slick2.png` },
    { property: 'og:image:alt', content: 'Slick Slicer logo' },
    { name: 'twitter:card', content: 'summary' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: `${siteUrl}/slick2.png` },
    { name: 'twitter:image:alt', content: 'Slick Slicer logo' },
    ...(canonical ? [{ property: 'og:url', content: canonical }] : []),
  ];
  const structuredData = page ? {
    '@context': 'https://schema.org',
    '@graph': [
      { '@type': 'Organization', '@id': `${siteUrl}/#organization`, name: 'Slick Slicer', url: `${siteUrl}/`, logo: `${siteUrl}/slick2.png`, email: 'support@slick-slicer.com', sameAs: ['https://www.youtube.com/@SlickSlicer-g8z', 'https://www.instagram.com/slick.slicer/', 'https://www.tiktok.com/@slickslicer35'] },
      { '@type': 'WebSite', '@id': `${siteUrl}/#website`, name: 'Slick Slicer', url: `${siteUrl}/`, inLanguage: 'en', publisher: { '@id': `${siteUrl}/#organization` } },
      { '@type': page.type, '@id': `${canonical}#webpage`, url: canonical, name: title, description, inLanguage: 'en', isPartOf: { '@id': `${siteUrl}/#website` } },
    ],
  } : undefined;
  return { title, canonical, meta, structuredData };
}

export function updateSeo(pathname: string) {
  const seo = getSeo(pathname);
  document.title = seo.title;
  document.head.querySelectorAll('[data-seo], meta[name="description"], link[rel="canonical"]').forEach(node => node.remove());
  seo.meta.forEach(attributes => {
    const element = document.createElement('meta');
    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
    element.setAttribute('data-seo', '');
    document.head.appendChild(element);
  });
  if (seo.canonical) {
    const link = document.createElement('link');
    link.rel = 'canonical';
    link.href = seo.canonical;
    link.setAttribute('data-seo', '');
    document.head.appendChild(link);
  }
  if (seo.structuredData) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', '');
    script.textContent = JSON.stringify(seo.structuredData);
    document.head.appendChild(script);
  }
}
